import { IInboxFileEntity, IUserEntity } from "@odata/GeneratedEntityTypes";
import { ApprovalStatusTypeCode } from "@odata/GeneratedEnums";
import { isDefined } from "@utils/general";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/button";
import { Status } from "../../enums";
import { getApprovalStatus } from "./Inbox.utils";
import { ButtonGroupStyled } from "@components/chat/Chat.styles";
import EmailBodyMessage from "@pages/inbox/EmailBodyMessage";


interface IProps {
    isAgendaCustomer?: boolean;
    file: IInboxFileEntity;
    maxHeight?: number;
    showEmailText?: boolean;
    onCreateMessage?: (message: string, status: ApprovalStatusTypeCode) => Promise<boolean>;
}

function getName(user: IUserEntity) {
    return user.Name ?? [user.FirstName, user.LastName].filter(isDefined).join(' ');
}

const InboxChat: React.FC<IProps> = ({
                                         file,
                                         maxHeight,
                                         onCreateMessage,
                                         isAgendaCustomer: _isAgendaCustomer,
                                         showEmailText
                                     }) => {
    const [newMessage, setNewMessage] = useState<string>("");
    const [busy, setBusy] = useState<boolean>(false);
    const { t, ready: tReady } = useTranslation("Inbox");

    const handleSendMessage = useCallback(async (status?: ApprovalStatusTypeCode) => {
        setBusy(true);
        const success = await onCreateMessage(newMessage, status);
        if (success) {
            setNewMessage("");
        }
        setBusy(false);
    }, [onCreateMessage, newMessage]);

    const handleAskForApproval = useCallback(() => {
        handleSendMessage(ApprovalStatusTypeCode.Pending);
    }, [handleSendMessage]);

    const handleReject = useCallback(() => {
        handleSendMessage(ApprovalStatusTypeCode.Rejected);
    }, [handleSendMessage]);

    const handleApprove = useCallback(async () => {
        handleSendMessage(ApprovalStatusTypeCode.Approved);
    }, [handleSendMessage]);

    const status = getApprovalStatus(file);
    let customButtons;
    switch (status) {
        case ApprovalStatusTypeCode.Pending:
            customButtons = _isAgendaCustomer && (<>
                <Button onClick={handleReject}
                        status={Status.Error}
                        isDisabled={busy}>{t("Inbox:Chat.Reject")}</Button>
                <Button onClick={handleApprove}
                        status={Status.Success}
                        isDisabled={busy}>{t("Inbox:Chat.Approve")}</Button>
            </>);
            break;
        case ApprovalStatusTypeCode.Rejected:
            customButtons = !_isAgendaCustomer && (
                <Button onClick={handleAskForApproval}
                        status={Status.Success}
                        isDisabled={busy}>{t("Inbox:Chat.AskForApproval")}</Button>
            );
            break;
    }

    return (
        <>
            {showEmailText && <EmailBodyMessage text={file.Body} created={file.FileMetadata?.DateCreated} createdBy={file.Sender} />}
            <ButtonGroupStyled>
                {customButtons}
            </ButtonGroupStyled>
        </>
    );
};

export default InboxChat;
